import { useCallback, useEffect, useRef, useState } from 'react';
import {Subtitle} from "@/models/Subtitle";
import {secondsToMs} from "@/utils/subtitleUtils";

interface DragState {
    itemIndex: number;
    type: 'move' | 'resize-start' | 'resize-end';
    startX: number;
    startTime: number;
    startEndTime?: number;
}

interface UseTimelineDraggingProps {
    items: Subtitle[];
    pixelsPerSecond: number;
    effectiveDuration: number;
    onItemUpdate: (index: number, updatedItem: Subtitle) => void;
    containerRef: React.RefObject<HTMLDivElement>;
}

export function useTimelineDragging({
    items,
    pixelsPerSecond,
    effectiveDuration,
    onItemUpdate,
    containerRef
}: UseTimelineDraggingProps) {
    const [isDragging, setIsDragging] = useState<DragState | null>(null);

    const handleMouseDown = useCallback((e: React.MouseEvent, itemIndex: number, type: 'move' | 'resize-start' | 'resize-end') => {
        e.preventDefault();
        e.stopPropagation();
        const item = items[itemIndex];
        setIsDragging({
            itemIndex,
            type,
            startX: e.clientX,
            startTime: item.startTimeSeconds,
            startEndTime: item.endTimeSeconds
        });
    }, [items]);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging || !containerRef.current) return;

        const deltaX = e.clientX - isDragging.startX;
        const deltaTime = deltaX / pixelsPerSecond;
        const item = items[isDragging.itemIndex];

        let newStartTime = isDragging.startTime;
        let newEndTime = isDragging.startEndTime!;

        // Overlaps are valid (concurrent cues), so drag/resize only clamps to the timeline
        // bounds and a minimum length — it no longer snaps against neighboring items.
        if (isDragging.type === 'move') {
            const targetStart = isDragging.startTime + deltaTime;
            const itemDuration = item.durationSeconds;

            newStartTime = Math.max(0, Math.min(targetStart, effectiveDuration - itemDuration));
            newEndTime = newStartTime + itemDuration;
        } else if (isDragging.type === 'resize-start') {
            const targetStart = isDragging.startTime + deltaTime;
            const maxStart = item.endTimeSeconds - 0.1;

            newStartTime = Math.max(0, Math.min(targetStart, maxStart));
        } else if (isDragging.type === 'resize-end') {
            const targetEnd = isDragging.startEndTime! + deltaTime;
            const minEnd = item.startTimeSeconds + 0.1;

            newEndTime = Math.max(minEnd, Math.min(targetEnd, effectiveDuration));
        }

        if (newStartTime !== item.startTimeSeconds || newEndTime !== item.endTimeSeconds) {
            const updatedItem: Subtitle = new Subtitle({
                ...item,
                startTimeMs: secondsToMs(newStartTime),
                endTimeMs: secondsToMs(newEndTime)
            });
            onItemUpdate(isDragging.itemIndex, updatedItem);
        }
    }, [isDragging, items, pixelsPerSecond, effectiveDuration, onItemUpdate, containerRef]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(null);
    }, []);

    // Set up global mouse event listeners when dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            const container = containerRef.current;
            if (container) {
                // Store initial scroll position and prevent scrolling during drag
                const initialScrollLeft = container.scrollLeft;
                let scrollLocked = false;

                const preventScroll = () => {
                    if (!scrollLocked) {
                        scrollLocked = true;
                        requestAnimationFrame(() => {
                            if (container && isDragging) {
                                container.scrollLeft = initialScrollLeft;
                            }
                            scrollLocked = false;
                        });
                    }
                };

                container.addEventListener('scroll', preventScroll, {passive: true});
                document.body.style.userSelect = 'none';

                return () => {
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);
                    container.removeEventListener('scroll', preventScroll);
                    document.body.style.userSelect = '';
                };
            }

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp, containerRef]);

    return {
        isDragging,
        handleMouseDown
    };
}
